// src/utils/ervEngine.js
// ============================================================
// Tenafi AI — Expected Recovery Value (ERV) Engine
// Ported from the ERV TypeScript engine, adapted to our
// Firestore tenant data structure.
//
// ERV = probability_of_recovery × outstanding_balance
// Tenants are ranked highest-ERV first so the most
// valuable and most-recoverable cases rise to the top.
// ============================================================

// ---- Normalise a Firestore tenant doc into the shape the engine expects ----
function normaliseTenant(tenant) {
  return {
    id: tenant.id,
    tenantName: tenant.name || 'Unknown',
    propertyId: tenant.propertyId || '',
    unitNumber: tenant.unit || '',
    currentBalance: tenant.currentBalance || 0,
    monthlyRent: tenant.monthlyRent || 0,
    riskScore: tenant.risk?.score || 0,
    riskTier: tenant.risk?.tier || 'minimal',
    daysLate: tenant.currentDaysLate || 0,
    // paymentHistory shape [{date, amount, daysLate}] is already compatible
    paymentHistory: tenant.paymentHistory || [],
  };
}

// ---- Flatten per-tenant outreach logs into the flat array the engine needs ----
export function flattenOutreachLogs(tenants) {
  return (tenants || []).flatMap(t =>
    (t.outreachLog || []).map(log => ({
      tenantId: t.id,
      sentAt: log.date,
      responseText: log.responseText || null,
      outcome: log.outcome || null,
    }))
  );
}

// ---- Weight constants ----
const WEIGHTS = {
  paymentHistory: 0.30,
  riskScore: 0.25,
  sentiment: 0.20,
  daysLate: 0.15,
  balanceRatio: 0.10,
};

// ---- Component scorers ----

function scorePaymentHistory(tenant) {
  const factors = [];
  const history = tenant.paymentHistory;

  if (history.length === 0) {
    factors.push({ label: 'No History', impact: 'neutral', weight: 0, description: 'No payment history available' });
    return { score: 50, factors };
  }

  const avgDaysLate = history.reduce((s, h) => s + (h.daysLate || 0), 0) / history.length;
  let historyScore = 100;

  if (avgDaysLate <= 2) {
    historyScore = 95;
    factors.push({ label: 'Excellent History', impact: 'positive', weight: 0.9, description: `Avg ${avgDaysLate.toFixed(1)}d late` });
  } else if (avgDaysLate <= 7) {
    historyScore = 75;
    factors.push({ label: 'Good History', impact: 'positive', weight: 0.5, description: `Avg ${avgDaysLate.toFixed(1)}d late` });
  } else if (avgDaysLate <= 15) {
    historyScore = 50;
    factors.push({ label: 'Fair History', impact: 'neutral', weight: 0, description: `Avg ${avgDaysLate.toFixed(1)}d late` });
  } else {
    historyScore = 25;
    factors.push({ label: 'Poor History', impact: 'negative', weight: -0.6, description: `Avg ${avgDaysLate.toFixed(1)}d late` });
  }

  const partialPayments = history.filter(h => h.amount > 0 && h.amount < tenant.monthlyRent);
  if (partialPayments.length > 0) {
    const ratio = partialPayments.length / history.length;
    historyScore -= ratio * 20;
    factors.push({ label: 'Partial Payments', impact: 'negative', weight: -ratio * 0.4, description: `${partialPayments.length} of ${history.length} were partial` });
  }

  // Trend detection (history sorted oldest-first; slice(-2) = most recent)
  if (history.length >= 3) {
    const recent = history.slice(-2).reduce((s, h) => s + (h.daysLate || 0), 0) / 2;
    const older = history.slice(0, 2).reduce((s, h) => s + (h.daysLate || 0), 0) / 2;
    if (recent < older - 2) {
      historyScore += 10;
      factors.push({ label: 'Improving Trend', impact: 'positive', weight: 0.3, description: 'Recent payments are earlier' });
    } else if (recent > older + 2) {
      historyScore -= 10;
      factors.push({ label: 'Worsening Trend', impact: 'negative', weight: -0.3, description: 'Recent payments are later' });
    }
  }

  return { score: Math.max(0, Math.min(100, historyScore)), factors };
}

function scoreRisk(tenant) {
  const factors = [];
  const invertedScore = 100 - tenant.riskScore;

  const tierMap = {
    minimal: { impact: 'positive', weight: 0.8 },
    low: { impact: 'positive', weight: 0.4 },
    medium: { impact: 'negative', weight: -0.2 },
    high: { impact: 'negative', weight: -0.7 },
  };

  const tier = tierMap[tenant.riskTier] || tierMap.minimal;
  const tierLabel = tenant.riskTier.charAt(0).toUpperCase() + tenant.riskTier.slice(1);
  factors.push({ label: `${tierLabel} Risk`, impact: tier.impact, weight: tier.weight, description: `Risk score: ${tenant.riskScore}/100` });

  return { score: invertedScore, factors };
}

function scoreDaysLate(tenant) {
  const factors = [];
  const days = tenant.daysLate;
  let score;

  if (days === 0) {
    score = 100;
    factors.push({ label: 'Current', impact: 'positive', weight: 0.9, description: 'Not overdue' });
  } else if (days <= 5) {
    score = 85;
    factors.push({ label: 'Slightly Late', impact: 'neutral', weight: 0.1, description: `${days} days overdue` });
  } else if (days <= 15) {
    score = 60;
    factors.push({ label: 'Overdue', impact: 'negative', weight: -0.3, description: `${days} days overdue` });
  } else if (days <= 30) {
    score = 35;
    factors.push({ label: 'Significantly Overdue', impact: 'negative', weight: -0.6, description: `${days} days overdue` });
  } else {
    score = 15;
    factors.push({ label: 'Severely Overdue', impact: 'negative', weight: -0.9, description: `${days} days overdue` });
  }

  return { score, factors };
}

function scoreBalanceRatio(tenant) {
  const factors = [];
  const ratio = tenant.monthlyRent > 0 ? tenant.currentBalance / tenant.monthlyRent : 0;
  let score;

  if (ratio <= 0) {
    score = 100;
    factors.push({ label: 'No Balance', impact: 'positive', weight: 0.9, description: 'No outstanding balance' });
  } else if (ratio <= 1) {
    score = 70;
    factors.push({ label: '1 Month Owed', impact: 'neutral', weight: 0, description: `${ratio.toFixed(1)}x rent owed` });
  } else if (ratio <= 2) {
    score = 45;
    factors.push({ label: '2 Months Owed', impact: 'negative', weight: -0.4, description: `${ratio.toFixed(1)}x rent owed` });
  } else {
    score = 20;
    factors.push({ label: '3+ Months Owed', impact: 'negative', weight: -0.8, description: `${ratio.toFixed(1)}x rent owed` });
  }

  return { score, factors };
}

function scoreSentiment(sentiment) {
  const factors = [];

  if (!sentiment) {
    factors.push({ label: 'No Response', impact: 'neutral', weight: 0, description: 'No sentiment data available' });
    return { score: 50, factors };
  }

  const sentimentScoreMap = { positive: 85, neutral: 55, negative: 30, hostile: 10 };
  const score = sentimentScoreMap[sentiment.sentiment] ?? 50;

  const sentLabel = sentiment.sentiment.charAt(0).toUpperCase() + sentiment.sentiment.slice(1);
  const impactMap = { positive: 'positive', neutral: 'neutral', negative: 'negative', hostile: 'negative' };
  factors.push({
    label: `${sentLabel} Sentiment`,
    impact: impactMap[sentiment.sentiment] || 'neutral',
    weight: sentiment.sentiment === 'positive' ? 0.6 : sentiment.sentiment === 'hostile' ? -0.9 : sentiment.sentiment === 'negative' ? -0.5 : 0,
    description: (sentiment.summary || '').slice(0, 80),
  });

  if (sentiment.paymentLikelihood === 'high') {
    factors.push({ label: 'High Payment Likelihood', impact: 'positive', weight: 0.5, description: 'Tenant likely to pay' });
  } else if (sentiment.paymentLikelihood === 'very_low') {
    factors.push({ label: 'Very Low Payment Likelihood', impact: 'negative', weight: -0.6, description: 'Low chance of payment' });
  }

  return { score, factors };
}

function determinePipelineStage(tenant, daysSinceOutreach, sentiment) {
  if (daysSinceOutreach === null || daysSinceOutreach > 14) return 'predict';
  if (!sentiment) return 'prioritize';
  return 'execute';
}

function determineUrgency(erv, riskScore, daysLate) {
  if (riskScore >= 85 && daysLate >= 20) return 'critical';
  if (riskScore >= 70 || daysLate >= 15 || erv >= 3000) return 'high';
  if (riskScore >= 50 || daysLate >= 7) return 'medium';
  return 'low';
}

function generateAction(tenant, stage, sentiment) {
  if (stage === 'predict') {
    if (tenant.riskTier === 'high') return 'Initiate firm outreach — high risk, no recent contact';
    if (tenant.riskTier === 'medium') return 'Send friendly reminder — moderate risk detected';
    return 'Monitor — low risk, schedule check-in';
  }
  if (stage === 'prioritize') {
    if (tenant.daysLate >= 20) return 'Escalate — no response after outreach, significantly overdue';
    return 'Follow up — awaiting tenant response';
  }
  if (sentiment) {
    if (sentiment.escalationRisk === 'critical' || sentiment.escalationRisk === 'high') {
      return 'Pause automation — escalation risk detected, manual review needed';
    }
    if (sentiment.paymentLikelihood === 'high') return 'Set payment reminder — tenant committed to pay';
    if (sentiment.paymentLikelihood === 'low' || sentiment.paymentLikelihood === 'very_low') {
      return 'Offer payment plan — low payment likelihood, explore options';
    }
    return sentiment.suggestedAction;
  }
  return 'Review account status';
}

// ---- Main: calculate ERV for a single tenant ----
export function calculateERV(tenant, outreachLogs = [], sentimentMap = new Map()) {
  const t = normaliseTenant(tenant);
  const sentiment = sentimentMap.get(t.id) ?? null;

  // Get last outreach date
  const tenantLogs = outreachLogs.filter(l => l.tenantId === t.id);
  const lastOutreach = tenantLogs.length > 0
    ? tenantLogs.sort((a, b) => new Date(b.sentAt).getTime() - new Date(a.sentAt).getTime())[0]
    : null;
  const daysSinceLastOutreach = lastOutreach
    ? Math.floor((Date.now() - new Date(lastOutreach.sentAt).getTime()) / (1000 * 60 * 60 * 24))
    : null;

  // Component scores
  const history = scorePaymentHistory(t);
  const risk = scoreRisk(t);
  const daysLateScore = scoreDaysLate(t);
  const balanceScore = scoreBalanceRatio(t);
  const sentimentScore = scoreSentiment(sentiment);

  // Weighted composite → recovery probability
  const rawProb = Math.round(
    history.score * WEIGHTS.paymentHistory +
    risk.score * WEIGHTS.riskScore +
    sentimentScore.score * WEIGHTS.sentiment +
    daysLateScore.score * WEIGHTS.daysLate +
    balanceScore.score * WEIGHTS.balanceRatio
  );
  const recoveryProbability = Math.max(2, Math.min(98, rawProb));

  // ERV
  const ervDollars = Math.round((recoveryProbability / 100) * t.currentBalance);

  const factors = [
    ...history.factors,
    ...risk.factors,
    ...daysLateScore.factors,
    ...balanceScore.factors,
    ...sentimentScore.factors,
  ];

  const pipelineStage = determinePipelineStage(t, daysSinceLastOutreach, sentiment);
  const urgency = determineUrgency(ervDollars, t.riskScore, t.daysLate);
  const recommendedAction = generateAction(t, pipelineStage, sentiment);

  return {
    tenantId: t.id,
    tenantName: t.tenantName,
    propertyId: t.propertyId,
    unitNumber: t.unitNumber,
    ervDollars,
    recoveryProbability,
    outstandingBalance: t.currentBalance,
    monthlyRent: t.monthlyRent,
    rank: 0, // set after sort
    pipelineStage,
    recommendedAction,
    factors,
    urgency,
    daysSinceLastOutreach,
    lastSentiment: sentiment,
  };
}

// ---- Calculate ERV for entire portfolio and build pipeline summary ----
export function calculatePortfolioERV(tenants, outreachLogs = [], sentimentMap = new Map()) {
  const results = tenants.map(t => calculateERV(t, outreachLogs, sentimentMap));
  results.sort((a, b) => b.ervDollars - a.ervDollars);
  results.forEach((r, i) => { r.rank = i + 1; });

  const predict = results.filter(r => r.pipelineStage === 'predict');
  const prioritize = results.filter(r => r.pipelineStage === 'prioritize');
  const execute = results.filter(r => r.pipelineStage === 'execute');

  const totalOutstanding = results.reduce((s, r) => s + r.outstandingBalance, 0);
  const totalRecoverable = results.reduce((s, r) => s + r.ervDollars, 0);

  return {
    predict: { count: predict.length, totalErv: predict.reduce((s, r) => s + r.ervDollars, 0), tenants: predict },
    prioritize: { count: prioritize.length, totalErv: prioritize.reduce((s, r) => s + r.ervDollars, 0), tenants: prioritize },
    execute: { count: execute.length, totalErv: execute.reduce((s, r) => s + r.ervDollars, 0), tenants: execute },
    totalRecoverable,
    totalOutstanding,
    portfolioRecoveryRate: totalOutstanding > 0 ? Math.round((totalRecoverable / totalOutstanding) * 100) : 100,
    allResults: results,
  };
}
